import { Box, Button, CircularProgress, Snackbar, Tooltip, Typography, makeStyles } from '@material-ui/core'
import { useApolloClient } from '@apollo/react-hooks'
import Alert from '@material-ui/lab/Alert'
import MuiAlert from '@material-ui/lab/Alert'
import React from 'react'
import { useParams } from 'react-router'
import Helmet from 'react-helmet'
import { useEnableDeviceAcks, useDisableDeviceAcks } from '../../../api/useDeviceAcks'
import { Link } from '../../link'
import { useGetDisplayGroup } from '../hooks/useGetDisplayGroup'
import { ParentFanoutCard } from './message-appearance-history/ParentFanoutCard'
import { FanoutStatsPanel } from './message-appearance-history/FanoutStatsPanel'
import { buildDisplayGroupLayout } from './message-appearance-history/displayGroupLayout'
import { applyNoAppearanceReasons } from './message-appearance-history/noAppearanceReason'
import { BOARD_DEVICES_QUERY } from './message-appearance-history/queries'
import type { BoardMeta } from './message-appearance-history/types'
import { MATCH_WINDOW_MS, useMessageAppearanceHistory } from './message-appearance-history/useMessageAppearanceHistory'
import type { BoardDevicesForAcks, BoardDevicesForAcksVariables } from './__generated__/BoardDevicesForAcks'

const useStyles = makeStyles(theme => ({
  title: {
    paddingBottom: 14
  },
  actionsRow: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
    marginBottom: 16
  }
}))

export const NoteArrayMessageAppearanceHistoryPage: React.FC = () => {
  const classes = useStyles()
  const client = useApolloClient()
  const { id } = useParams<{ id: string }>()

  const [enableAcks] = useEnableDeviceAcks()
  const [disableAcks] = useDisableDeviceAcks()
  const [bulkAckLoading, setBulkAckLoading] = React.useState(false)
  const [bulkAckMessage, setBulkAckMessage] = React.useState<{ text: string; severity: 'success' | 'error' } | null>(null)
  const [deviceMap, setDeviceMap] = React.useState<Record<string, string>>({})

  const { data: displayGroupData, loading: displayGroupLoading, error: displayGroupError } = useGetDisplayGroup(id)
  const displayGroup =
    displayGroupData?.getDisplayGroup?.__typename === 'DisplayGroupV2' ? displayGroupData.getDisplayGroup : null

  const childBoards = React.useMemo<BoardMeta[]>(() => {
    const boards = displayGroup?.boards?.flat()?.filter(Boolean) || []
    const seen = new Set<string>()
    const out: BoardMeta[] = []
    for (const board of boards) {
      if (!board?.id || seen.has(board.id)) continue
      seen.add(board.id)
      out.push({
        id: board.id,
        title: board.board.title || null
      })
    }
    return out
  }, [displayGroup])

  const {
    records,
    nextCursor,
    loadingInitial,
    loadingMore,
    assembling,
    error: historyError,
    loadMore
  } = useMessageAppearanceHistory({
    client,
    arrayBoardId: id,
    childBoards
  })

  const displayGroupLayout = React.useMemo(
    () => (displayGroup ? buildDisplayGroupLayout(displayGroup) : null),
    [displayGroup]
  )

  const enrichedRecords = React.useMemo(
    () => applyNoAppearanceReasons(records, displayGroupLayout),
    [displayGroupLayout, records]
  )

  const actualBoardIds = React.useMemo(() => {
    if (!displayGroup) return []
    return displayGroup.boards
      .flat()
      .filter(Boolean)
      .map(entry => entry.board.id)
      .filter((boardId, index, arr) => arr.indexOf(boardId) === index)
  }, [displayGroup])

  React.useEffect(() => {
    if (actualBoardIds.length === 0) return

    let alive = true
    const resolveDevices = async () => {
      const results: Record<string, string> = {}
      await Promise.all(
        actualBoardIds.map(async boardId => {
          try {
            const result = await client.query<BoardDevicesForAcks, BoardDevicesForAcksVariables>({
              query: BOARD_DEVICES_QUERY,
              variables: { boardId },
              fetchPolicy: 'cache-first'
            })
            const deviceId = result.data?.board?.devices?.[0]?.id
            if (deviceId) results[boardId] = deviceId
          } catch {}
        })
      )
      if (alive) {
        setDeviceMap(results)
      }
    }
    void resolveDevices()

    return () => {
      alive = false
    }
  }, [actualBoardIds, client])

  const allDeviceIds = React.useMemo(() => Object.values(deviceMap), [deviceMap])

  const handleBulkAcks = async (enable: boolean) => {
    if (allDeviceIds.length === 0) {
      setBulkAckMessage({ text: 'No devices found yet', severity: 'error' })
      return
    }
    setBulkAckLoading(true)
    const mutation = enable ? enableAcks : disableAcks
    let successCount = 0
    let failCount = 0
    for (const deviceId of allDeviceIds) {
      try {
        await mutation({ variables: { device: deviceId } })
        successCount += 1
      } catch {
        failCount += 1
      }
    }
    setBulkAckLoading(false)
    if (failCount === 0) {
      setBulkAckMessage({
        text: `Acks ${enable ? 'enabled' : 'disabled'} on ${successCount} device(s)`,
        severity: 'success'
      })
    } else {
      setBulkAckMessage({ text: `${successCount} succeeded, ${failCount} failed`, severity: 'error' })
    }
  }

  if (displayGroupLoading) {
    return <CircularProgress />
  }

  if (displayGroupError || !displayGroup) {
    const errorMessage =
      displayGroupData?.getDisplayGroup?.__typename === 'DisplayGroupErrorV2'
        ? displayGroupData.getDisplayGroup.error
        : 'Note Array not found.'
    return <Alert severity='error'>{errorMessage}</Alert>
  }

  return (
    <Box>
      <Helmet>
        <title>Message Appearance History: {displayGroup.title || 'Untitled'}</title>
      </Helmet>
      <Typography variant='button' display='block' gutterBottom>
        <Link to={`/note-arrays/${displayGroup.id}`}>Note Array</Link>
      </Typography>
      <Typography variant='h4' className={classes.title}>
        Message Appearance History
      </Typography>
      <Typography variant='body2' color='textSecondary' gutterBottom>
        Group child board appearances within {MATCH_WINDOW_MS}ms of each parent array appearance. Acks reflect when
        the device reported receipt, independent of display delay.
      </Typography>

      <Box className={classes.actionsRow}>
        <Tooltip title='The ack state is managed on device — iterates through all devices in this note array' arrow>
          <span>
            <Button
              variant='contained'
              color='primary'
              size='small'
              disabled={bulkAckLoading || allDeviceIds.length === 0}
              onClick={() => {
                void handleBulkAcks(true)
              }}>
              {bulkAckLoading ? <CircularProgress size={18} /> : 'Enable Acks (All Devices)'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title='The ack state is managed on device — iterates through all devices in this note array' arrow>
          <span>
            <Button
              variant='contained'
              size='small'
              disabled={bulkAckLoading || allDeviceIds.length === 0}
              onClick={() => {
                void handleBulkAcks(false)
              }}>
              {bulkAckLoading ? <CircularProgress size={18} /> : 'Disable Acks (All Devices)'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Snackbar open={!!bulkAckMessage} autoHideDuration={4000} onClose={() => setBulkAckMessage(null)}>
        <MuiAlert onClose={() => setBulkAckMessage(null)} severity={bulkAckMessage?.severity}>
          {bulkAckMessage?.text}
        </MuiAlert>
      </Snackbar>

      {historyError ? <Alert severity='warning'>{historyError}</Alert> : null}

      {!loadingInitial && enrichedRecords.length > 0 ? <FanoutStatsPanel records={enrichedRecords} /> : null}

      {loadingInitial ? <CircularProgress /> : null}
      {!loadingInitial && enrichedRecords.length === 0 ? (
        <Alert severity='info'>No array-level message appearances found.</Alert>
      ) : null}

      {enrichedRecords.map(record => (
        <ParentFanoutCard key={record.parent.id} record={record} />
      ))}

      <Box display='flex' justifyContent='center'>
        <Button
          variant='outlined'
          disabled={!nextCursor || loadingMore || loadingInitial || assembling}
          onClick={() => {
            void loadMore()
          }}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </Button>
      </Box>
      {assembling ? (
        <Box mt={2} display='flex' justifyContent='center'>
          <CircularProgress size={20} />
        </Box>
      ) : null}
    </Box>
  )
}
